import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip);

// Colors for different categories
const colorPalette = [
  'rgb(59, 130, 246)',   // blue-500
  'rgb(16, 185, 129)',   // green-500
  'rgb(239, 68, 68)',    // red-500
  'rgb(245, 158, 11)',   // amber-500
  'rgb(139, 92, 246)',   // purple-500
  'rgb(236, 72, 153)',   // pink-500
  'rgb(14, 165, 233)',   // sky-500
  'rgb(168, 85, 247)',   // violet-500
];

const DonutChart = ({ data, category, onClick }) => {
  const chartData = {
    labels: data.map(item => item.name),
    datasets: [{
      data: data.map(item => item.count),
      backgroundColor: data.map((_, index) => colorPalette[index % colorPalette.length]),
      borderColor: data.map((_, index) => colorPalette[index % colorPalette.length].replace('rgb', 'rgba').replace(')', ', 0.2)')),
      borderWidth: 1,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        onClick(data[index].name);
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return <Doughnut data={chartData} options={options} />;
};

const ChartLegend = ({ data, onItemClick }) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);
  
  return (
    <div className="flex flex-col text-sm w-[140px]">
      {data.slice(0, 14).map((item, index) => {
        const percentage = ((item.count / total) * 100).toFixed(1);
        const fullText = `${item.name} (${percentage}%)`;
        
        return (
          <div 
            key={item.name} 
            className="flex items-center mb-1 last:mb-0 cursor-pointer hover:opacity-80"
            title={fullText}
            onClick={() => onItemClick(item.name)}
          >
            <div 
              className="flex-shrink-0 w-2.5 h-2.5 rounded-full mr-2" 
              style={{ backgroundColor: colorPalette[index % colorPalette.length] }}
            />
            <span className="truncate">
              {fullText}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const CollectionCompositionChart = ({ data, category, legendPosition = 'right' }) => {
  const navigate = useNavigate();

  const handleChartClick = (value) => {
    // Create a new URLSearchParams to preserve other parameters like userId
    const params = new URLSearchParams(window.location.search);
    
    // Clear any existing filter parameters
    params.delete('metal');
    params.delete('country');
    params.delete('denomination');
    params.delete('certification service');
    
    // Add the new filter parameter
    if (category.toLowerCase() === 'certification service') {
      if (value === 'Uncertified') {
        params.set('TPG', 'isEmpty');
      } else {
        params.set('TPG', value);
      }
    } else {
      params.set(category.toLowerCase(), value);
    }
    
    // Navigate with the updated parameters
    navigate(`/inventory?${params.toString()}`);
  };
  // Sort data by count in descending order
  const sortedData = [...(data || [])].sort((a, b) => b.count - a.count);

  return (
    <div className="h-[400px] w-full">
      <div className="h-full grid" style={{ 
        gridTemplateColumns: legendPosition === 'left' ? 'auto 1fr' : '1fr auto',
        gap: '1rem'
      }}>
        {legendPosition === 'left' && (
          <div className="flex items-center">
            <ChartLegend data={sortedData} onItemClick={handleChartClick} />
          </div>
        )}
        <div className="relative">
          <h3 className="text-lg font-medium capitalize absolute w-full text-center -top-8">{category} Distribution</h3>
          <div className="absolute inset-0">
            <DonutChart 
              data={sortedData} 
              category={category}
              onClick={handleChartClick}
            />
          </div>
        </div>
        {legendPosition === 'right' && (
          <div className="flex items-center">
            <ChartLegend data={sortedData} onItemClick={handleChartClick} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionCompositionChart;
